import type {
  ClubId,
  FurnitureDef,
  IglooFloorId,
  IglooPlacement,
  IglooStyle,
  IglooSurfaceDef,
  IglooWallId,
} from "@clubap/shared";
import { economyGet, economyPost } from "./api.js";

/**
 * Llamadas de la sala de un club.
 *
 * Es el gemelo de `igloo.ts` con dos diferencias que vienen del dueño: todas llevan el
 * club en la ruta —una persona puede estar en tres— y hay una que el iglú no tiene,
 * aportar a la caja. Lo que el servidor deja hacer no se decide acá: `canDecorate` viene
 * en la respuesta para que la UI no ofrezca lo que va a rebotar, pero quien manda es él.
 */

export interface ClubRoomSnapshot {
  furniture: FurnitureDef[];
  owned: Record<string, number>;
  placements: IglooPlacement[];
  style: IglooStyle;
  floors: IglooSurfaceDef<IglooFloorId>[];
  walls: IglooSurfaceDef<IglooWallId>[];
  maxPlaced: number;
  /** Lo que hay en la caja común. Es del club, no de nadie. */
  treasury: number;
  /** Si QUIEN PREGUNTA puede decorar: hoy, si preside. */
  canDecorate: boolean;
}

export function fetchClubRoom(clubId: ClubId): Promise<ClubRoomSnapshot> {
  return economyGet<ClubRoomSnapshot>("club", `/${clubId}`);
}

/** Lo que queda en la caja del club y lo que le queda a quien acaba de aportar. */
export interface ClubDeposit {
  treasury: number;
  balance: number;
}

export function depositToClub(clubId: ClubId, amount: number): Promise<ClubDeposit> {
  return economyPost<ClubDeposit>("club", `/${clubId}/deposit`, { amount });
}

export function buyClubFurniture(
  clubId: ClubId,
  furnitureId: string,
): Promise<{ furniture: FurnitureDef; treasury: number }> {
  return economyPost("club", `/${clubId}/buy`, { furnitureId });
}

interface PlacementsResult {
  placements: IglooPlacement[];
}

export function placeClubFurniture(
  clubId: ClubId,
  furnitureId: string,
  x: number,
  y: number,
): Promise<IglooPlacement[]> {
  return economyPost<PlacementsResult>("club", `/${clubId}/place`, { furnitureId, x, y }).then(
    (result) => result.placements,
  );
}

export function moveClubFurniture(
  clubId: ClubId,
  placementId: string,
  x: number,
  y: number,
): Promise<IglooPlacement[]> {
  return economyPost<PlacementsResult>("club", `/${clubId}/move`, { placementId, x, y }).then(
    (result) => result.placements,
  );
}

export function removeClubFurniture(
  clubId: ClubId,
  placementId: string,
): Promise<IglooPlacement[]> {
  return economyPost<PlacementsResult>("club", `/${clubId}/remove`, { placementId }).then(
    (result) => result.placements,
  );
}

export function setClubStyle(
  clubId: ClubId,
  floor: IglooFloorId,
  wall: IglooWallId,
): Promise<IglooStyle> {
  return economyPost<{ style: IglooStyle }>("club", `/${clubId}/style`, { floor, wall }).then(
    (result) => result.style,
  );
}
